import {
  Box,
  chakra,
  Divider,
  Heading,
  HStack,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useStyleConfig,
  VStack,
} from '@chakra-ui/react';
import { IconPlusLarge } from 'central-icons/IconPlusLarge';
import mixpanel from 'mixpanel-browser';
import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import type { RouteNavConfig } from '@revelio/auth';
import {
  BuildADashTrackingEvents,
  FeatureFlag,
  PageTitles,
  TourClasses,
  useTourStore,
} from '@revelio/core';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { usePageManager } from '../../../CustomDash/components/PageManagerProvider/PageManagerProvider';
import {
  FilterId,
  type PrimaryFilterId,
} from '../../../CustomDash/filters/types/filters';
import {
  getPageConfigForPath,
  type PublicCustomDashPath,
} from '../../../CustomDash/utils/page-config-lookup';
import { ANALYTICS_VIEWS } from '../../../CustomDash/utils/revelio-primary-filter-entity-switch';
import {
  entityTypeToFilterId,
  extractEntityTypeFromPath,
  filterIdToEntityType,
} from '../../../CustomDash/utils/routing-helpers';
import { NavIconChild, navIconBg } from '../nav-icon';
import SubNavDropdown from '../sub-nav-dropdown/sub-nav-dropdown';
import CustomDashSubNav from './CustomDashSubNav';
import { SubNavItem } from './SubNavItem';
import styles from './sub-nav.module.css';

interface SubNavProps {
  parent: RouteNavConfig;
  items: RouteNavConfig[];
}

// Analytics rail items are configured with `<entity>/summary` paths so that
// each entity link lands on its overview by default. When the user is already
// on a known analytics view (e.g. `company/compositions`), preserve that view
// when they click a different entity in the rail by rewriting the trailing
// segment — e.g. clicking "Geography" from `/company/compositions` lands on
// `/geography/compositions` instead of resetting them to the overview.
const ANALYTICS_VIEW_SEGMENTS = new Set(
  ANALYTICS_VIEWS.flatMap((view) =>
    Object.values(view.paths).map((p) => p.split('/')[1])
  )
);

const resolveRailItemPath = (
  itemPath: string,
  pathname: string,
  parentName: string | undefined
): string => {
  if (parentName !== PageTitles.DASHBOARD_NAV) return itemPath;

  const [itemEntity, itemView] = itemPath.split('/');
  if (itemView !== 'summary') return itemPath;

  const pathEntity = extractEntityTypeFromPath(pathname);
  if (!entityTypeToFilterId(pathEntity)) return itemPath;

  const currentView = pathname.split('/')[2];
  if (!currentView || !ANALYTICS_VIEW_SEGMENTS.has(currentView)) {
    return itemPath;
  }

  return `${itemEntity}/${currentView}`;
};

export const SubNav = (props: SubNavProps) => {
  const { createPageDraft, clonePageDraft, clonePage, pages } =
    usePageManager();
  const customDashNavEnabled = useUnleashFlag(
    FeatureFlag.CustomDashViewEditToggle
  );
  const isNewDashboardLayout = useUnleashFlag(FeatureFlag.DashboardNewLayout);
  // The Pages popover in the Header owns page creation in the new layout, so
  // suppress the sidebar's create button there.
  const showSidebarCreatePage = customDashNavEnabled && !isNewDashboardLayout;
  const linkStyles = useStyleConfig('Link', { variant: 'nav' });
  const location = useLocation();
  const [isSubNavOpen, setIsSubNavOpen] = useState<boolean>(false);
  const iconRef = useRef<HTMLAnchorElement>(null);

  const [isActive, setIsActive] = useState(false);
  const [isControlPanelSpotlighted, setIsControlPanelSpotlighted] =
    useState(false);

  const filterIdMap: Record<string, PrimaryFilterId> = {
    [PageTitles.COMPANY]: FilterId.COMPANY,
    [PageTitles.GEOGRAPHY]: FilterId.GEOGRAPHY,
    [PageTitles.ROLE]: FilterId.ROLE,
    [PageTitles.DASHBOARD_NAV]: FilterId.COMPANY,
  };

  const primaryFilter = filterIdMap[props.parent.name as string];

  const tourIsTour = useTourStore((s) => s.isTour);
  const tourIsSideBarOpen = useTourStore((s) => s.isSideBarOpen);
  const tourIsSideBarCompany = useTourStore((s) => s.isSideBarCompany);
  const tourIsControlPanelSpotlighted = useTourStore(
    (s) => s.isControlPanelSpotlighted
  );

  useEffect(() => {
    if (
      tourIsTour &&
      props.parent.name === TourClasses.TOUR_DELIVERABLES_SUB_NAV_CLASS &&
      !tourIsSideBarCompany
    ) {
      setIsSubNavOpen(tourIsSideBarOpen);
    }
    if (
      tourIsTour &&
      props.parent.name === PageTitles.COMPANY &&
      tourIsSideBarCompany
    ) {
      setIsSubNavOpen(tourIsSideBarOpen);
    }
    if (
      tourIsTour &&
      isNewDashboardLayout &&
      props.parent.name === PageTitles.DASHBOARD_NAV &&
      tourIsSideBarCompany
    ) {
      setIsSubNavOpen(tourIsSideBarOpen);
    }
    setIsControlPanelSpotlighted(tourIsControlPanelSpotlighted);
  }, [
    tourIsTour,
    tourIsSideBarOpen,
    tourIsSideBarCompany,
    tourIsControlPanelSpotlighted,
    isNewDashboardLayout,
    props.parent.name,
  ]);

  useEffect(() => {
    if (!location) return;

    let match = false;

    // Match exact path or any nested route under it (so a parent stays
    // active when the user navigates into one of its sub-routes, e.g.
    // /report-builder/report-type still highlights the Reports parent).
    const pathMatches = (path: string | undefined) => {
      if (!path) return false;
      const fullPath = '/' + path;
      return (
        location.pathname === fullPath ||
        location.pathname.startsWith(fullPath + '/')
      );
    };

    if (props.items) {
      match = props.items.some((item) => {
        if (item.path) {
          return pathMatches(item.path);
        }
        if (item.children && Array.isArray(item.children)) {
          return item.children.some((child) => pathMatches(child.path));
        }
        return false;
      });
    }

    // CustomDash route handling
    if (!match && /\/(?:company|geography|role)\//.test(location.pathname)) {
      const idMatch = location.pathname.match(
        /^\/(?:company|geography|role)\/([^/]+)/
      );
      const pageId = idMatch?.[1];

      if (pageId) {
        const pageConfig = pages.find((p) => p.id === pageId);
        const pagePrimaryFilter =
          pageConfig?.tabs?.[0]?.tabContent?.primaryFilter?.id;

        if (pagePrimaryFilter && pagePrimaryFilter === primaryFilter) {
          match = true;
        }
      }
    }

    setIsActive(match);
  }, [location, props.items, pages, primaryFilter]);

  const visibleItems = props.items.filter(
    (item) => !item.excludeFromSideNavButtons
  );
  const firstItemIsSectionWithSubheading = visibleItems[0]?.subHeading;

  // Suppresses the `:hover` reopen after a click until the cursor
  // leaves and re-enters.
  const [isClosed, setIsClosed] = useState(false);

  const closeOnItemClick = () => {
    setIsClosed(true);
    setIsSubNavOpen(false);
  };

  return (
    <>
      {!props.parent.element && props.parent.divider && (
        <Divider color="white" opacity={0.3} width="70%" pointerEvents="none" />
      )}
      <Box
        className={styles.wrapper}
        data-testid={`${props.parent.name}-subnav-wrapper`}
        data-subnav-open={isSubNavOpen ? 'true' : undefined}
        data-tour-open={tourIsTour && isSubNavOpen ? 'true' : undefined}
        data-closed={isClosed ? 'true' : undefined}
        onPointerEnter={() => {
          setIsClosed(false);
          setIsSubNavOpen(true);
        }}
        onPointerLeave={() => {
          // Don't auto-close during a tour; the cursor moves to the Joyride
          // tooltip (portalled to <body>), which would otherwise close the drawer
          // and lose the tour step's target.
          if (tourIsTour) return;
          setIsClosed(false);
          setIsSubNavOpen(false);
        }}
      >
        {props.parent.element ? (
          <Link
            as={NavLink}
            to={props.parent.path || ''}
            bgImg={navIconBg(props.parent.icon)}
            variant="nav"
            position="relative"
            ref={iconRef}
            // probably should use a js utility to conditionally join these cleaner
            className={`nav ${
              // eslint-disable-next-line no-nested-ternary
              // biome-ignore lint/style/noNestedTernary: migrated from develop
              isActive ? 'active' : isSubNavOpen ? 'semi-active' : ''
            }`}
          >
            <NavIconChild icon={props.parent.icon} />
          </Link>
        ) : (
          <chakra.a
            sx={linkStyles}
            // probably should use a js utility to conditionally join these cleaner
            data-testid="rl-sub-nav-link"
            className={`nav ${
              // eslint-disable-next-line no-nested-ternary
              // biome-ignore lint/style/noNestedTernary: migrated from develop
              isActive ? 'active' : isSubNavOpen ? 'semi-active' : ''
            }`}
            href={props.parent.path}
            bgImg={navIconBg(props.parent.icon)}
            position="relative"
            h={props.parent.divider ? '6%' : '12%'}
            ref={iconRef}
            _before={
              isControlPanelSpotlighted && props.parent.isSourcesTab
                ? {
                    content: `"${props.parent.name}"`,
                    position: 'absolute',
                    fontSize: '9px',
                    color: '#fff',
                    top: props.parent.name === 'Company' ? '100%' : '78%',
                    left: '-4px',
                    textAlign: 'center',
                    width: '120%',
                  }
                : {}
            }
            _after={{
              content: '""',
              display: 'block',
              position: 'absolute',
              left: '7px',
              width: '48px',
              height: props.parent.divider ? '6vh' : '12vh',
              marginTop: '-20px',
            }}
          >
            <NavIconChild icon={props.parent.icon} />
          </chakra.a>
        )}

        <Box
          className={styles.pane}
          data-testid={`${props.parent.name}-subnav`}
          height="100vh"
          width="280px"
          position="absolute"
          background="white"
          left="100%"
          top="0"
          zIndex={9999}
          padding="32px 24px"
          boxShadow="2xl"
          _after={{
            content: '""',
            position: 'absolute',
            top: (iconRef?.current?.offsetTop || 0) + 14 + 'px',
            right: '100%',
            borderWidth: '6px',
            borderStyle: 'solid',
            borderColor: 'transparent white  transparent transparent',
          }}
          sx={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#E3E3E3 transparent',
            '&::-webkit-scrollbar': {
              width: '8px',
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'transparent',
              borderRadius: '10px',
              border: '2px solid transparent',
              backgroundClip: 'padding-box',
            },
            '&:hover::-webkit-scrollbar-thumb': {
              backgroundColor: '#E3E3E3',
            },
          }}
        >
          <VStack
            alignItems="flex-start"
            h="100%"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            minH={0}
            className={
              TourClasses.TOUR_DATA_SOURCES_CLASS +
              `${props.parent.name ? ' ' + props.parent.name : ''}`
            }
          >
            {!firstItemIsSectionWithSubheading && (
              <HStack w="100%" mb="0">
                <Heading color="text.primary" size="md" pb="2" flexGrow={1}>
                  {props.parent.name}
                </Heading>
                {showSidebarCreatePage && primaryFilter && (
                  <Box
                    position="relative"
                    display="inline-block"
                    data-testid="create-new-page-icon-button"
                  >
                    {isNewDashboardLayout ? (
                      <Menu placement="bottom-start" gutter={4}>
                        <MenuButton
                          as={IconButton}
                          aria-label="Create Page"
                          icon={<IconPlusLarge size={16} />}
                          size="sm"
                          mb="2"
                          colorScheme="sidebar"
                          title="Create a new page"
                          variant="ghost"
                        />
                        <MenuList minW="124px">
                          {(
                            [
                              {
                                label: 'Company',
                                filterId: FilterId.COMPANY,
                              },
                              {
                                label: 'Geography',
                                filterId: FilterId.GEOGRAPHY,
                              },
                              { label: 'Role', filterId: FilterId.ROLE },
                            ] as const
                          ).map(({ label, filterId }) => (
                            <MenuItem
                              key={filterId}
                              data-testid={`create-new-page-option-${label.toLowerCase()}`}
                              onClick={() => {
                                closeOnItemClick();
                                createPageDraft(filterIdToEntityType(filterId));
                                mixpanel.track(
                                  BuildADashTrackingEvents.CREATE_NEW_PAGE,
                                  { entityType: filterId }
                                );
                              }}
                            >
                              {label}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    ) : (
                      <IconButton
                        aria-label="Create Page"
                        icon={<IconPlusLarge size={16} />}
                        size="sm"
                        mb="2"
                        colorScheme="sidebar"
                        title="Create a new page"
                        variant="ghost"
                        onClick={() => {
                          closeOnItemClick();
                          createPageDraft(filterIdToEntityType(primaryFilter));
                          mixpanel.track(
                            BuildADashTrackingEvents.CREATE_NEW_PAGE,
                            { entityType: primaryFilter }
                          );
                        }}
                      />
                    )}
                  </Box>
                )}
              </HStack>
            )}

            {visibleItems.map((item, i) => {
              if (item.children) {
                return (
                  <SubNavDropdown
                    key={`${item.name}-${i}`}
                    item={item}
                    onSubNavClose={closeOnItemClick}
                    isParentOpen={isSubNavOpen}
                  />
                );
              }

              const pageConfig = getPageConfigForPath(
                item.path as PublicCustomDashPath
              );

              return (
                <React.Fragment key={`${item.name}-${i}`}>
                  {item.subHeading && (
                    <>
                      {!firstItemIsSectionWithSubheading && (
                        <Divider
                          borderColor="text.lowContrast"
                          opacity={1}
                          mt="5"
                          mb="6"
                        />
                      )}
                      <HStack w="100%" mb="0" justifyContent="space-between">
                        <Heading color="text.primary" size="md" pb="2">
                          {item.subHeading}
                        </Heading>
                      </HStack>
                    </>
                  )}
                  {item.divider && (
                    <Divider
                      borderColor="text.lowContrast"
                      opacity={1}
                      mt="10px"
                      mb="2px"
                    />
                  )}
                  <SubNavItem
                    to={resolveRailItemPath(
                      item.path as string,
                      location.pathname,
                      props.parent.name as string | undefined
                    )}
                    onClick={closeOnItemClick}
                    data-testid={`subnav-${item.parent ? `${item.parent}-` : ''}${item.name}`}
                    menuContent={
                      customDashNavEnabled && pageConfig ? (
                        <>
                          <MenuItem
                            onClick={() => {
                              clonePageDraft(pageConfig.id);
                              mixpanel.track(
                                BuildADashTrackingEvents.CLONE_PAGE,
                                {
                                  page_id: pageConfig.id,
                                  revelio_page_id: pageConfig.revelioPageId,
                                }
                              );
                            }}
                          >
                            Edit Page
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              clonePage(pageConfig.id);
                              mixpanel.track(
                                BuildADashTrackingEvents.CLONE_PAGE,
                                {
                                  page_id: pageConfig.id,
                                  revelio_page_id: pageConfig.revelioPageId,
                                }
                              );
                            }}
                          >
                            Make a Copy
                          </MenuItem>
                        </>
                      ) : undefined
                    }
                  >
                    {item.name}
                  </SubNavItem>
                </React.Fragment>
              );
            })}

            {customDashNavEnabled && (
              <>
                <Divider opacity="0" mt="5" mb="6" flexShrink={0} />
                <CustomDashSubNav
                  parentName={props.parent.name ?? ''}
                  primaryFilter={primaryFilter}
                  onLinkClick={closeOnItemClick}
                  isOpen={isSubNavOpen}
                />
              </>
            )}
          </VStack>
        </Box>
      </Box>
    </>
  );
};
